import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import type { RootState } from "../../../../../redux/store";
import TrashIcon from "../../../../../assets/svg/trash-icon";
import type { FilterFieldOption } from "../../HelpDesk/components/CreateView/utils/constants";
import { HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS } from "../../HelpDesk/components/CreateView/utils/constants";
import {
  formatFilterBetweenValueDisplay,
  getDurationPopperInitialForFilter,
} from "../../HelpDesk/components/CreateView/utils/functions";
import { FilterDropdown } from "../../HelpDesk/components/CreateView/components/FilterConditionRow";
import DurationPopper from "../../../../../components/DurationPopper/DurationPopper";
import type { DurationOption } from "../../../../../components/DurationPopper/utils/constants";
import MultiSelectSearchableDropdown from "../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";
import { useGetPropertiesListQuery } from "../../../../../redux/services/playgroundApi";
import { useGetListingFiltersQuery } from "../../../../../redux/services/communicationApi";
import { useGetAllAgentsQuery } from "../../../../../redux/services/userManagementApi";
import {
  useGetDealStagesQuery,
  useGetDealDistinctCitiesQuery,
  useGetDealDistinctAvailabilityTitlesQuery,
  useGetDealDistinctAcademicYearsQuery,
  useGetDealCancellationReasonsQuery,
} from "../../../../../redux/services/dealsApi";
import { getCurrencySymbol } from "../../../../../utils/functions/get-currency-symbols";
import { getDefaultCurrencyUnit } from "../../../../../utils/constants/mappings";
import { getDealCurrencyIconByRegion } from "../utils/functions";
import { convertLocalToUtcIsoString } from "../../../../../utils/functions/date-time";
import type { DealStageConfig } from "../utils/types";
import type {
  FilterConditionUI,
  FilterGroupUI,
} from "../utils/dealsViewConstants";
import {
  DEALS_VIEW_FILTER_FIELD_OPTIONS,
  DEALS_VALUE_OPTIONS_MAP,
  DEALS_SINGLE_VALUE_FIELDS,
  DEALS_DATE_RANGE_FIELDS,
  DEALS_DISABLED_FIELDS,
  DEALS_FREE_TEXT_FIELDS,
  DURATION_OPTIONS,
  EXISTS_VALUE_OPTIONS,
  getDealsOperatorsForField,
} from "../utils/dealsViewConstants";
import { DealAmountRangePopover } from "./DealAmountRangePopover";

const CURRENCY_ICON_FIELD_KEYS = ["total_amount", "deal_total_amount", "deal_weekly_rent"];

interface DealsFilterConditionRowProps {
  condition: FilterConditionUI;
  onChange: (updated: FilterConditionUI) => void;
  onDelete: () => void;
  showBackground?: boolean;
  fieldOptions?: FilterFieldOption[];
  filterGroups?: FilterGroupUI[];
}

export const DealsFilterConditionRow = ({
  condition,
  onChange,
  onDelete,
  showBackground = true,
  fieldOptions: fieldOptionsProp,
}: DealsFilterConditionRowProps) => {
  const clientRegion = useSelector(
    (state: RootState) => state.auth?.client?.region
  );
  const authCurrentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  const currencySymbol = getCurrencySymbol(
    getDefaultCurrencyUnit(clientRegion) || "GBP"
  );
  const RegionCurrencyIcon = getDealCurrencyIconByRegion(clientRegion);

  const [betweenDurationOpen, setBetweenDurationOpen] = useState(false);
  const [autoSelectedKey, setAutoSelectedKey] = useState<string | null>(null);

  const fieldOptions = useMemo(() => {
    const base = fieldOptionsProp ?? DEALS_VIEW_FILTER_FIELD_OPTIONS;
    return base.map((f) =>
      CURRENCY_ICON_FIELD_KEYS.includes(f.key)
        ? { ...f, icon: RegionCurrencyIcon }
        : f
    );
  }, [fieldOptionsProp, RegionCurrencyIcon]);
  const selectedField = fieldOptions?.find((f) => f?.key === condition?.key);

  const needsAgents = condition?.key === "assignee";
  const needsStages = condition?.key === "stage";
  const needsProperties = condition?.key === "property_name";
  const needsListingFilters = condition?.key === "room_type";
  const needsCities = condition?.key === "city";
  const needsAvailabilityTitles = condition?.key === "availability_title";
  const needsAcademicYears = condition?.key === "academic_year";
  const needsCancellationReasons = condition?.key === "cancelled_primary_reason";

  const { data: agentsData, isLoading: isAgentsLoading } = useGetAllAgentsQuery(
    undefined,
    { skip: !needsAgents }
  );

  const { data: stagesData, isLoading: isStagesLoading } =
    useGetDealStagesQuery(undefined, { skip: !needsStages });

  const { data: propertiesData, isLoading: isPropertiesLoading } =
    useGetPropertiesListQuery(
      { managerId: authCurrentClientCode ?? "", page: 1, pageSize: 1000 },
      { skip: !needsProperties || !authCurrentClientCode }
    );

  const utmQueryArgs = useMemo(() => {
    const now = new Date();
    const past = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    return {
      start_date: past.toISOString(),
      end_date: now.toISOString(),
      chat_type: "leads_created",
    };
  }, []);

  const { data: listingFiltersData, isLoading: isListingFiltersLoading } =
    useGetListingFiltersQuery(utmQueryArgs, { skip: !needsListingFilters });

  const { data: citiesData, isLoading: isCitiesLoading } =
    useGetDealDistinctCitiesQuery(undefined, { skip: !needsCities });

  const {
    data: availabilityTitlesData,
    isLoading: isAvailabilityTitlesLoading,
  } = useGetDealDistinctAvailabilityTitlesQuery(undefined, {
    skip: !needsAvailabilityTitles,
  });

  const { data: academicYearsData, isLoading: isAcademicYearsLoading } =
    useGetDealDistinctAcademicYearsQuery(undefined, {
      skip: !needsAcademicYears,
    });

  const {
    data: cancellationReasonsData,
    isLoading: isCancellationReasonsLoading,
  } = useGetDealCancellationReasonsQuery(undefined, {
    skip: !needsCancellationReasons,
  });

  const agentOptions = useMemo(() => {
    if (!agentsData?.items) return [];
    return agentsData.items.map(
      (u: { _id: string; name: string; email: string }) => ({
        key: u._id || u.email,
        label: u.name || u.email,
      })
    );
  }, [agentsData]);

  const stageOptions = useMemo(() => {
    if (!stagesData) return [];
    return (stagesData as DealStageConfig[]).map((s) => ({
      key: s?.key,
      label: s?.title,
      color: s?.secondary_hex_code,
    }));
  }, [stagesData]);

  const propertyOptions = useMemo(() => {
    if (!propertiesData?.res) return [];
    return propertiesData.res.map((p: { id: number; name: string }) => ({
      key: String(p.id),
      label: p.name,
    }));
  }, [propertiesData]);

  const roomTypeOptions = useMemo(() => {
    if (!listingFiltersData?.room_types) return [];
    return listingFiltersData.room_types.map((rt: string) => ({
      key: rt,
      label: rt
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c: string) => c.toUpperCase()),
    }));
  }, [listingFiltersData]);

  const cityOptions = useMemo(() => {
    if (!citiesData?.values) return [];
    return citiesData.values.map((city: string) => ({
      key: city,
      label: city,
    }));
  }, [citiesData]);

  const availabilityTitleOptions = useMemo(() => {
    if (!availabilityTitlesData?.values) return [];
    return availabilityTitlesData.values.map((title: string) => ({
      key: title,
      label: title,
    }));
  }, [availabilityTitlesData]);

  const academicYearOptions = useMemo(() => {
    if (!academicYearsData?.values) return [];
    return academicYearsData.values.map((year: string) => ({
      key: year,
      label: year,
    }));
  }, [academicYearsData]);

  const cancellationReasonOptions = useMemo(() => {
    if (!cancellationReasonsData?.primary_reasons) return [];
    return cancellationReasonsData.primary_reasons.map((reason: string) => ({
      key: reason,
      label: reason,
    }));
  }, [cancellationReasonsData]);

  const valueOptions = useMemo(() => {
    const staticOpts = DEALS_VALUE_OPTIONS_MAP[condition?.key];
    if (staticOpts) return staticOpts;
    if (condition?.key === "assignee") return agentOptions;
    if (condition?.key === "stage") return stageOptions;
    if (condition?.key === "room_type") return roomTypeOptions;
    if (condition?.key === "property_name") return propertyOptions;
    if (condition?.key === "city") return cityOptions;
    if (condition?.key === "availability_title")
      return availabilityTitleOptions;
    if (condition?.key === "academic_year") return academicYearOptions;
    if (condition?.key === "cancelled_primary_reason")
      return cancellationReasonOptions;
    if (
      condition?.key === "created_at" ||
      condition?.key === "updated_at" ||
      condition?.key === "contract_start" ||
      condition?.key === "contract_end" ||
      condition?.key === "cancellation_effective_date"
    )
      return DURATION_OPTIONS;
    return [];
  }, [
    condition?.key,
    agentOptions,
    stageOptions,
    roomTypeOptions,
    propertyOptions,
    cityOptions,
    availabilityTitleOptions,
    academicYearOptions,
    cancellationReasonOptions,
  ]);

  const operatorOptions = useMemo(
    () => getDealsOperatorsForField(condition?.key ?? ""),
    [condition?.key]
  );

  const isValueLoading = useMemo(() => {
    if (!condition?.key) return false;
    if (needsAgents && isAgentsLoading) return true;
    if (needsStages && isStagesLoading) return true;
    if (needsProperties && isPropertiesLoading) return true;
    if (needsListingFilters && isListingFiltersLoading) return true;
    if (needsCities && isCitiesLoading) return true;
    if (needsAvailabilityTitles && isAvailabilityTitlesLoading) return true;
    if (needsAcademicYears && isAcademicYearsLoading) return true;
    if (needsCancellationReasons && isCancellationReasonsLoading) return true;
    return false;
  }, [
    condition?.key,
    needsAgents,
    isAgentsLoading,
    needsStages,
    isStagesLoading,
    needsProperties,
    isPropertiesLoading,
    needsListingFilters,
    isListingFiltersLoading,
    needsCities,
    isCitiesLoading,
    needsAvailabilityTitles,
    isAvailabilityTitlesLoading,
    needsAcademicYears,
    isAcademicYearsLoading,
    needsCancellationReasons,
    isCancellationReasonsLoading,
  ]);

  const valueLoadingPlaceholder = selectedField?.label ?? "Value";
  const isDurationField = DEALS_DATE_RANGE_FIELDS.includes(condition?.key);
  const isBetweenDateField =
    isDurationField && condition?.comparison_operator === "range";
  const isSingleValueField = DEALS_SINGLE_VALUE_FIELDS.includes(condition?.key);
  const isAmountField = condition?.key === "total_amount";
  // Only free-text fields render a text box on `contains` / `not_contains`.
  // Other fields keep their dropdown / multi-select; `contains_any` / `not_in`
  // always stay multi-select.
  const isContainsTextInput =
    DEALS_FREE_TEXT_FIELDS.includes(condition?.key) &&
    (condition?.comparison_operator === "contains" ||
      condition?.comparison_operator === "not_contains");
  const isExistsOperator = condition?.comparison_operator === "exists";

  const isStandardValueDropdown =
    !isAmountField && !isBetweenDateField && !isContainsTextInput && !isExistsOperator;

  useEffect(() => {
    if (!isBetweenDateField) setBetweenDurationOpen(false);
  }, [isBetweenDateField]);

  useEffect(() => {
    if (!condition?.key) return;
    if (isValueLoading) return;
    if (!isStandardValueDropdown) return;
    if (autoSelectedKey === condition?.key) return;
    if ((condition?.value?.length ?? 0) > 0) {
      setAutoSelectedKey(condition?.key);
      return;
    }
    if (valueOptions?.length !== 1) return;
    setAutoSelectedKey(condition?.key);
    onChange({ ...condition, value: [valueOptions[0]?.key] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    condition?.key,
    valueOptions,
    isValueLoading,
    isStandardValueDropdown,
    condition?.value?.length,
    autoSelectedKey,
  ]);

  const durationPopperInitial = useMemo(() => {
    const summary = formatFilterBetweenValueDisplay(condition?.value ?? []);
    if (!summary) {
      return {
        initialOption: { id: "custom", label: "Custom" } as DurationOption,
      };
    }
    return getDurationPopperInitialForFilter(condition?.value ?? []);
  }, [condition?.value]);

  const hasBetweenDurationFilterValue = useMemo(
    () => Boolean(formatFilterBetweenValueDisplay(condition?.value ?? [])),
    [condition?.value]
  );

  const handleFieldChange = (key: string) => {
    if (DEALS_DISABLED_FIELDS[key]) return;
    const defaultOp = getDealsOperatorsForField(key)[0]?.key ?? "contains";
    onChange({
      ...condition,
      key,
      value: [],
      comparison_operator: defaultOp,
      is_custom_field: false,
    });
  };

  const handleOperatorChange = (op: string) => {
    const isDateField = DEALS_DATE_RANGE_FIELDS.includes(condition?.key);
    const wasExists = condition?.comparison_operator === "exists";
    const isExists = op === "exists";
    onChange({
      ...condition,
      comparison_operator: op,
      ...(isDateField || wasExists || isExists ? { value: [] } : {}),
    });
  };

  const handleSingleValueChange = (val: string) => {
    onChange({ ...condition, value: [val] });
  };

  const handleValueChange = (vals: string[]) => {
    onChange({ ...condition, value: vals });
  };

  return (
    <div
      className={`flex items-center gap-2 h-[48px] ${showBackground ? "bg-hover-effect dark:bg-hover-effect-dk rounded-lg p-2" : ""}`}
    >
      <FilterDropdown
        options={fieldOptions?.map((f) => ({ key: f?.key, label: f?.label }))}
        value={condition?.key}
        onChange={handleFieldChange}
        placeholder="Select filter"
        width="min-w-[150px] max-w-[150px]"
        dropdownClassName="!min-w-[210px] !max-w-[260px]"
        renderOption={(opt) => {
          const field = fieldOptions?.find((f) => f?.key === opt?.key);
          if (!field) return opt?.label;
          const Icon = field?.icon;
          const tooltipText = DEALS_DISABLED_FIELDS[opt?.key];
          const content = (
            <div
              className={`flex items-center gap-1.5 w-full ${tooltipText ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Icon className="shrink-0 w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              <span className="truncate">{opt?.label}</span>
            </div>
          );
          if (tooltipText) {
            return (
              <Tooltip content={tooltipText} side="right">
                {content}
              </Tooltip>
            );
          }
          return content;
        }}
        renderSelected={(opt) => {
          if (!selectedField) return opt?.label;
          const Icon = selectedField?.icon;
          return (
            <>
              <Icon className="shrink-0 w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
              <span className="truncate">{opt?.label}</span>
            </>
          );
        }}
      />

      {condition?.key &&
        (operatorOptions?.length === 1 ? (
          <span className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] max-w-[90px] flex items-center justify-center px-2 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk select-none whitespace-nowrap">
            {operatorOptions?.[0]?.label}
          </span>
        ) : (
          <FilterDropdown
            options={operatorOptions}
            value={condition?.comparison_operator}
            onChange={handleOperatorChange}
            placeholder="Operator"
            width="w-[90px]"
            textClassName="font-medium"
          />
        ))}

      {condition?.key &&
        (isExistsOperator ? (
          <FilterDropdown
            options={EXISTS_VALUE_OPTIONS}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder="Select"
            width="min-w-[100px] max-w-[100px]"
          />
        ) : isContainsTextInput ? (
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={condition?.value?.[0] || ""}
              onChange={(e) => handleSingleValueChange(e.target.value)}
              placeholder={valueLoadingPlaceholder}
              className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors"
            />
          </div>
        ) : isAmountField ? (
          <DealAmountRangePopover
            value={condition?.value || []}
            onChange={handleValueChange}
            currencySymbol={currencySymbol}
          />
        ) : isBetweenDateField ? (
          <DurationPopper
            key={`${condition.key}-${condition.value?.join("|") ?? ""}-between`}
            variant="default"
            isOpen={betweenDurationOpen}
            onOpen={() => setBetweenDurationOpen(true)}
            onClose={() => setBetweenDurationOpen(false)}
            applyOnMount={false}
            durationOptions={
              HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS as DurationOption[]
            }
            title="Duration"
            triggerName={valueLoadingPlaceholder}
            triggerAriaLabel={valueLoadingPlaceholder}
            initialDates={durationPopperInitial.initialDates}
            initialOption={durationPopperInitial.initialOption}
            onApply={(from, to, selectedDurationOption) => {
              const optionId = selectedDurationOption?.id ?? "custom";
              onChange({
                ...condition,
                value: [
                  convertLocalToUtcIsoString(
                    format(from, "yyyy-MM-dd'T'HH:mm:ss")
                  ),
                  convertLocalToUtcIsoString(
                    format(to, "yyyy-MM-dd'T'HH:mm:ss")
                  ),
                  optionId,
                ],
              });
            }}
            portalHorizontalAlign="end"
            className="flex-1 min-w-0 self-center"
            triggerClassName="!rounded-lg !h-[32px] !max-h-[32px] !min-h-[32px] w-full !bg-pure-white dark:!bg-pure-white-dk !backdrop-blur-none border border-solid border-border-gray dark:border-border-gray-dk !shadow-none px-0"
            triggerIconClassName="!w-[14px] !h-[14px] !shrink-0 !text-verba-indigo dark:!text-verba-indigo-dk !transition-transform"
            selectedOptionContainerClassName="!h-full !min-h-0 !max-w-[150px] min-w-0 !justify-between !px-2 !py-0 items-center"
            triggerTextClassName={
              hasBetweenDurationFilterValue
                ? "text-md font-medium !text-verba-indigo dark:!text-verba-indigo-dk"
                : "text-md font-medium !text-gray-placeholder dark:!text-gray-placeholder-dk"
            }
          />
        ) : isDurationField || isSingleValueField ? (
          <FilterDropdown
            options={isDurationField ? DURATION_OPTIONS : valueOptions}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder={valueLoadingPlaceholder}
            width="min-w-[150px] max-w-[150px]"
            isLoading={isValueLoading}
          />
        ) : (
          <div className="min-w-[150px] max-w-[150px]">
            <MultiSelectSearchableDropdown
              disabled={isValueLoading}
              optionClassName={
                condition?.key === "stage"
                  ? "!px-3 !py-1.5 !gap-1.5 !h-[34px]"
                  : ""
              }
              options={
                isValueLoading
                  ? []
                  : valueOptions?.map(
                      (opt: { key: string; label: string; color?: string }) => {
                        if (condition?.key === "stage") {
                          const stagePill = (
                            <span className="flex flex-1 items-center gap-1 h-[20px] py-0.5 rounded-md min-w-0">
                              <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{
                                  backgroundColor: opt?.color || "#666",
                                }}
                              />
                              <TruncatedTextTooltip
                                text={opt?.label}
                                className="text-xs font-semibold uppercase text-verba-indigo dark:text-verba-indigo-dk leading-[18px]"
                                maxWidth="max-w-full"
                                side="left"
                              />
                            </span>
                          );
                          return {
                            value: opt?.key,
                            label: opt?.label,
                            selectedOptionContent: opt?.label,
                            content: stagePill,
                          };
                        }
                        return {
                          value: opt?.key,
                          label: opt?.label,
                          selectedOptionContent: opt?.label,
                          content: (
                            <span className="flex items-center gap-1.5 text-md min-w-0 text-verba-indigo dark:text-verba-indigo-dk">
                              <TruncatedTextTooltip text={opt?.label} />
                            </span>
                          ),
                        };
                      }
                    )
              }
              value={condition?.value || []}
              onChange={(vals) => handleValueChange(vals)}
              placeholder={
                isValueLoading
                  ? `Loading ${valueLoadingPlaceholder}...`
                  : needsCancellationReasons
                    ? "Select primary reason"
                    : "Select value"
              }
              showSearch={!isValueLoading && valueOptions?.length > 4}
              showSelectedOptionsChips={true}
              autoFitChips
              moreTagText=""
              chipClassName="inline-flex items-center px-0 py-0 text-md font-medium rounded-none border-none bg-transparent text-heading-text dark:text-heading-text-dk shrink min-w-0 [&>button]:hidden after:content-[','] last:after:content-[''] after:ml-[1.5px]"
              moreTagClassName="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md bg-tag-gray dark:bg-tag-gray-dk text-heading-text dark:text-heading-text-dk shrink-0 font-medium"
              triggerClassName="!rounded-lg h-[32px]"
              inputContainerClassName="!py-0 h-full rounded-lg !bg-pure-white dark:!bg-pure-white-dk"
              placeholderClassName="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk"
              selectedOptionClassName="!bg-card-highlight-2 dark:!bg-card-highlight-2-dk !border-b-selected-option-highlight dark:!border-b-selected-option-highlight-dk"
              dropdownClassName="!max-h-[200px] !min-w-[170px]"
              optionsListClassName="!py-0"
              className="w-full"
            />
          </div>
        ))}

      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 w-[14px] h-[14px] flex items-center justify-center cursor-pointer text-dark-gray dark:text-dark-gray-dk hover:text-verba-red-alert dark:hover:text-verba-red-alert-dk transition-colors"
      >
        <TrashIcon className="w-[14px] h-[14px]" />
      </button>
    </div>
  );
};
